// /src/Components/Clients/AdminWhatsApp.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  Building2,
  Filter,
  RefreshCcw,
  Search,
  Megaphone,
  ClipboardList,
  BadgeIndianRupee,
} from "lucide-react";
import { GlobalContext } from "../../Context/GlobalContext";
import CampaignChargesModal from "./CampaignChargesModal";

const PAGE_SIZE = 100;

export default function AdminWhatsApp() {
  const { AllClients } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");

  const [chargesModal, setChargesModal] = useState({
    open: false,
    userId: "",
    title: "Campaign Charges",
  });

  useEffect(() => {
    setClients(AllClients);
  }, [AllClients]);

  const filtered = useMemo(() => {
    const s = (v) => String(v ?? "").toLowerCase();
    const ql = q.trim().toLowerCase();
    return clients?.filter(
      (c) => !ql || s(c.us_name).includes(ql) || s(c.us_code).includes(ql)
    );
  }, [clients, q]);

  const totalPages = Math.max(1, Math.ceil(filtered?.length / PAGE_SIZE));
  const [page, setPage] = useState(1);

  const pageRows = useMemo(
    () => filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const clear = () => {
    setQ("");
    setPage(1);
  };

  const openGlobalCharges = () => {
    setChargesModal({
      open: true,
      userId: "",
      title: "Campaign Charges",
    });
  };

  const openClientCharges = (userId, clientName) => {
    setChargesModal({
      open: true,
      userId: userId || "",
      title: `Campaign Charges - ${clientName || userId || "Client"}`,
    });
  };

  const closeChargesModal = () => {
    setChargesModal({
      open: false,
      userId: "",
      title: "Campaign Charges",
    });
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">WhatsApp</h1>
            <p className="text-sm text-gray-500">
              Pick a client to manage campaigns, templates & charges
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={openGlobalCharges}
          >
            <BadgeIndianRupee size={16} /> Charges
          </button>

          <button
            className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={() => setClients(AllClients)}
            disabled={loading}
          >
            <RefreshCcw size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={16} /> Filters
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <div className="flex h-10 w-80 items-center gap-2 rounded-xl border px-3">
              <Search size={16} className="text-gray-500" />
              <input
                className="h-full w-full text-sm outline-none"
                placeholder="Client name or code…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </label>

          <div className="ml-auto flex gap-2">
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
              onClick={clear}
              disabled={loading}
            >
              Clear
            </button>
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90"
              onClick={() => {}}
              disabled
              title="Filters auto-apply"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[14%]" />
              <col className="w-[14%]" />
              <col className="w-[14%]" />
              <col className="w-[30%]" />
            </colgroup>

            <thead className="bg-[#01C37D] text-white">
              <tr className="whitespace-nowrap">
                {["Client Name", "Code", "Campaigns", "Templates", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && pageRows?.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No clients found.
                  </td>
                </tr>
              )}

              {pageRows?.map((c, idx) => (
                <tr key={`${c.us_code}-${idx}`} className="border-t align-top">
                  <td className="px-4 py-2">{c.us_name || "—"}</td>
                  <td className="px-4 py-2">{c.us_code || "—"}</td>
                  <td className="px-4 py-2">{c.totalCampaigns ?? "0"}</td>
                  <td className="px-4 py-2">{c.totalTemplates ?? "0"}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => navigate(`/admin/campaigns/${c.us_code}`)}
                        title="View Campaigns"
                      >
                        <Megaphone size={14} /> Campaigns
                      </button>

                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => navigate(`/admin/templates/${c.us_code}`)}
                        title="View Templates"
                      >
                        <ClipboardList size={14} /> Templates
                      </button>

                      <button
                        className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1 text-xs hover:bg-gray-50"
                        onClick={() => openClientCharges(c.us_code, c.us_name)}
                        title="Manage Charges"
                      >
                        <BadgeIndianRupee size={14} /> Charges
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filtered?.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b>
            </span>
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <CampaignChargesModal
        open={chargesModal.open}
        onClose={closeChargesModal}
        userId={chargesModal.userId}
        title={chargesModal.title}
      />
    </div>
  );
}